import React, { useState } from 'react';

import {
  BaseTextFieldProps,
  ButtonBase,
  MenuItem,
  Popover,
  TextField,
  Typography,
  InputAdornment,
  Box,
  IconButton,
  Zoom,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import {
  defaultCountries,
  FlagEmoji,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';

const useStyles = makeStyles(({ spacing, typography, palette }) => ({
  textField: {
    marginBottom: spacing(2),
  },
  input: {
    fontSize: typography.pxToRem(14),
  },
  popover: {
    marginTop: spacing(0.5),
    width: 390,
    maxHeight: 420,
    padding: spacing(1),
    overflow: 'hidden',
  },
  searchContainer: {
    padding: spacing(1),
    backgroundColor: palette.background.paper,
  },
  searchField: {
    width: '100%',
  },
  itemsContainer: {
    maxHeight: 350,
    overflowY: 'auto',
  },
  flagButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    padding: spacing(1),
    borderRadius: spacing(0.5),
    backgroundColor: palette.background.paper,
    cursor: 'pointer',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
    padding: spacing(1),
  },
  selectPadding: {
    marginRight: spacing(1),
  },
  emptyMessage: {
    padding: spacing(2),
    textAlign: 'center',
    color: palette.text.secondary,
  },
}));

export interface InternacionalPhoneInputProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
}

export default function InternacionalPhoneInput(
  props: InternacionalPhoneInputProps,
) {
  const { value, onChange, ...restProps } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filter, setFilter] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const open = Boolean(anchorEl);
  const id = open ? 'country-selector-popover' : undefined;

  const { phone, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: 'br',
      value,
      countries: defaultCountries,
      onChange: data => {
        onChange(data.phone);
      },
    });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleResetFilter = () => {
    setFilter('');
    setHighlightedIndex(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleResetFilter();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!open) return;

    const filteredCountries = defaultCountries.filter(c => {
      const countryData = parseCountry(c);
      const searchTerm = filter.toLowerCase();
      return (
        countryData.name.toLowerCase().includes(searchTerm) ||
        countryData.dialCode.includes(searchTerm)
      );
    });

    switch (event.key) {
      case 'ArrowDown':
        setHighlightedIndex(prev =>
          prev < filteredCountries.length - 1 ? prev + 1 : prev,
        );
        event.preventDefault();
        break;
      case 'ArrowUp':
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        event.preventDefault();
        break;
      case 'Enter':
        if (filteredCountries[highlightedIndex]) {
          const selectedCountry = parseCountry(
            filteredCountries[highlightedIndex],
          );
          setCountry(selectedCountry.iso2);
          handleClose();
        }
        event.preventDefault();
        break;
      case 'Escape':
        handleClose();
        break;
      default:
        break;
    }
  };

  const filteredCountries = defaultCountries.filter(c => {
    const countryData = parseCountry(c);
    const searchTerm = filter.toLowerCase();
    return (
      countryData.name.toLowerCase().includes(searchTerm) ||
      countryData.dialCode.includes(searchTerm)
    );
  });

  return (
    <TextField
      variant="outlined"
      fullWidth
      label="Contato"
      color="primary"
      placeholder="Número de telefone"
      value={phone}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      InputLabelProps={{
        className: classes.input,
      }}
      className={classes.textField}
      InputProps={{
        className: classes.input,
        startAdornment: (
          <InputAdornment
            position="start"
            style={{ marginRight: '2px', marginLeft: '-8px' }}
          >
            <ButtonBase
              aria-describedby={id}
              onClick={handleClick}
              className={classes.flagButton}
            >
              <FlagEmoji
                size={24}
                iso2={country}
                className={classes.selectPadding}
              />
              {open ? (
                <ArrowDropUpIcon color="action" />
              ) : (
                <ArrowDropDownIcon color="action" />
              )}
            </ButtonBase>
            <Popover
              id={id}
              classes={{ paper: classes.popover }}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onKeyDown={handleKeyDown}
            >
              <Box className={classes.searchContainer}>
                <TextField
                  autoFocus={open}
                  InputProps={{
                    classes: { input: classes.input },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment:
                      (filter && (
                        <Zoom in mountOnEnter unmountOnExit timeout={400}>
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleResetFilter}
                              size="small"
                            >
                              <CloseIcon fontSize="small" color="action" />
                            </IconButton>
                          </InputAdornment>
                        </Zoom>
                      )) ||
                      undefined,
                  }}
                  variant="outlined"
                  placeholder="Buscar país ou código"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className={classes.searchField}
                  size="small"
                />
              </Box>

              <Box className={classes.itemsContainer}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c, index) => {
                    const countryData = parseCountry(c);

                    return (
                      <MenuItem
                        key={countryData.iso2}
                        value={countryData.iso2}
                        selected={index === highlightedIndex}
                        className={classes.menuItem}
                        onClick={() => {
                          setCountry(countryData.iso2);
                          handleClose();
                        }}
                      >
                        <FlagEmoji
                          size={24}
                          iso2={countryData.iso2}
                          className={classes.selectPadding}
                        />
                        <Typography variant="body2">
                          {countryData.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          +{countryData.dialCode}
                        </Typography>
                      </MenuItem>
                    );
                  })
                ) : (
                  <Typography className={classes.emptyMessage} variant="body2">
                    Nenhum país encontrado
                  </Typography>
                )}
              </Box>
            </Popover>
          </InputAdornment>
        ),
      }}
      {...restProps}
    />
  );
}
